// ClientsListPageCached.js

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  Card,
  CardHeader,
  CardContent,
  MenuItem,
  Chip,
  IconButton,
  Menu,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { readSessionSort, writeSessionSort, clearSessionSort } from '../utils/sessionSort';
import { formatDistanceToNow, format as formatDate } from 'date-fns';
import { he } from 'date-fns/locale';
import {
  DataGridPro,
  useGridApiRef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-pro';
import { Link, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useTranslation } from 'react-i18next';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useUser } from '../contexts/UserContext';
import * as XLSX from 'xlsx';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import ClientCreateDialog from './dialogs/ClientCreateDialog';
import PaymentDialog from './dialogs/PaymentDialog';
import ChargeDialog from './dialogs/ChargeDialog';
import CreditDialog from './dialogs/CreditDialog';
import DebtFilterPanel from '../components/DebtFilterPanel';
import ClientsSummaryBar from './components/ClientsSummaryBar';
import ClientInsightsPanel from './components/ClientInsightsPanel';
import ResponsiveDataView from '../components/ResponsiveDataView';
import ResponsiveToolbar, { ToolbarActions } from '../components/ResponsiveToolbar';
import { useIsPhone } from '../hooks/useIsMobile';

// ==================== THEME ====================
const theme = createTheme({
  direction: 'rtl',
  ...heIL,
  typography: {
    fontSize: 16,
  },
});

// ==================== CUSTOM TOOLBAR ====================
function CustomToolbar({
  rows = [],
  columns = [],
  isExporting,
  setIsExporting,
  selectedGroup,
  groupName,
  debtFilters = [],
  ordering = '-last_interaction_at',
}) {
  const navigate = useNavigate();

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const response = await API.get('/clients/export-excel/', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'לקוחות_ייצוא.xlsx');
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
      alert('שגיאה ביצוא קובץ - נסה שוב');
    } finally {
      setIsExporting(false);
    }
  };

  // Build print URL with all filters
  const buildPrintUrl = () => {
    const params = new URLSearchParams();
    if (selectedGroup) params.append('group', selectedGroup);
    if (groupName) params.append('groupName', groupName);
    if (ordering) params.append('ordering', ordering);
    // Pass multiple debt filters as JSON
    const validFilters = debtFilters.filter(f => f.field && f.threshold);
    if (validFilters.length > 0) {
      params.append('debtFilters', JSON.stringify(validFilters));
    }
    return `/clients/minimal-print?${params.toString()}`;
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <Button
        variant="text"
        color="primary"
        size="small"
        onClick={handleExportExcel}
        disabled={isExporting}
      >
        {isExporting ? <CircularProgress size={24} /> : 'אקסל'}
      </Button>
      <Button
        variant="text"
        color="primary"
        size="small"
        onClick={() => navigate(buildPrintUrl())}
      >
        הדפסה
      </Button>
    </GridToolbarContainer>
  );
}

// ==================== FETCH CLIENTS ====================
// Autocomplete search function - fetches clients based on search term
async function fetchClientsAutocomplete(searchTerm = '') {
  const params = {
    light: true,  // Use light serializer for better performance
    limit: 100  // Always show 100 clients for consistency
  };

  if (searchTerm) {
    params.search = searchTerm;
  }

  const response = await API.get('/clients/', { params });
  return response.data;
}

// Export function - fetches ALL clients with light data (for export)
export async function fetchClients() {
  const response = await API.get('/clients/', { params: { light: true } });
  return response.data;
}

// Paginated fetch function for main list
async function fetchPaginatedClients({
  page = 1,
  pageSize = 25,
  search = '',
  groupFilter = '',
  ordering = 'ClientName',
  debtFilters = [], // Now an array of { field, threshold }
  clientIds = null, // Array of ClientBusinessIDs to filter by (used by "show all" insights)
}) {
  const params = new URLSearchParams({
    page: page,
    page_size: pageSize,
    ordering: ordering,
  });

  if (search) params.append('search', search);
  if (groupFilter) params.append('group_filter', groupFilter);

  // Add multiple debt filters
  if (debtFilters.length > 0) {
    const validFilters = debtFilters.filter(f => f.field && f.threshold);
    if (validFilters.length > 0) {
      params.append('debt_filters', JSON.stringify(validFilters));
    }
  }

  // Filter by specific client IDs (insights "show all")
  if (clientIds && clientIds.length > 0) {
    params.append('client_ids', JSON.stringify(clientIds));
  }

  const response = await API.get(`/clients/list/?${params.toString()}`);
  return response.data;
}

// Default ordering for the clients list — most-recently-interacted first.
// Backed by GetClientsListView's `last_interaction_at` annotation.
const DEFAULT_CLIENTS_ORDERING = '-last_interaction_at';

// Bump when the default column layout changes in a way that saved
// orderedFields must be migrated (see initialGridState).
const GRID_LAYOUT_VERSION = 2;

// Helper function to convert grid sortModel to API ordering parameter
const getOrderingFromSortModel = (sortModel) => {
  if (!sortModel || sortModel.length === 0) {
    return DEFAULT_CLIENTS_ORDERING;
  }
  const { field, sort } = sortModel[0];
  return sort === 'desc' ? `-${field}` : field;
};

// Convert an API ordering string back to a DataGridPro sortModel entry.
// Always returns a populated sortModel so the column-header arrow stays
// in sync — including for the default sort. (Previously we returned []
// when ordering matched the default, which hid the arrow on Last Active
// even though that was the active sort.)
const getSortModelFromOrdering = (ordering) => {
  if (!ordering) return [];
  const isDesc = ordering.startsWith('-');
  const field = isDesc ? ordering.slice(1) : ordering;
  return [{ field, sort: isDesc ? 'desc' : 'asc' }];
};

const ClientsListPageCached = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const role = userProfile?.role;
  const queryClient = useQueryClient();
  const isPhone = useIsPhone();

  // Track initial mount to prevent resetting pagination on first render
  const isInitialMount = React.useRef(true);

  // ===== Read ui_config for dynamic fields =====
  const uiConfig = userProfile?.ui_config || {};
  const debtFields = uiConfig.debt_fields || [];
  const debtFieldsSchema = uiConfig.debt_fields_schema || {};
  const clientFieldsSchema = uiConfig.client_fields_schema || {};
  const clientExtraColumns = uiConfig.client_list_page?.client_extra_columns || [];
  const createClientRoles = uiConfig.pages_configs?.ClientsListPageCached?.create_client_roles || ["manager"];
  const groupAccessRoles = uiConfig.pages_configs?.ClientsListPageCached?.group_acces_roles || ["manager"];
  const ClientAllowedRoles = uiConfig.pages_configs?.ClientDetailPage?.allowed_roles || ["manager", "employee"];
  const summaryRoles = uiConfig.pages_configs?.ClientsListPageCached?.summary_roles || ["manager"];
  const actionButtonsRoles = uiConfig.pages_configs?.TransactionsPage?.action_buttons_roles || ["manager"];
  const noPaymentMonthsThreshold = uiConfig.pages_configs?.ClientsListPageCached?.no_payment_months_threshold || 2;
  const highDebtThreshold = uiConfig.pages_configs?.ClientsListPageCached?.high_debt_threshold || 0;
  const rowHighlightCategories = uiConfig.pages_configs?.ClientsListPageCached?.row_highlight_categories || ['no_recent_payment', 'highest_debt'];
  const showSummary = summaryRoles.includes(role);
  const showActions = actionButtonsRoles.includes(role);

  // Row coloring — IDs provided by ClientInsightsPanel via callback
  const [highlightIds, setHighlightIds] = useState({});
  const gridRef = React.useRef(null);
  // Insight filter — when "הצג הכל" is clicked, filter the grid to show only those clients
  const [insightFilter, setInsightFilter] = useState(null); // { category, ids, label }

  const handleHighlightIdsChange = useCallback((ids) => {
    setHighlightIds(ids);
  }, []);

  const getRowClassName = useCallback((params) => {
    const id = params.row?.ClientBusinessID;
    if (!id) return '';

    // When a specific insight filter is active ("Show All"), all filtered rows get that category's color
    if (insightFilter) {
      if (insightFilter.category === 'no_recent_payment') return 'row-no-payment';
      if (insightFilter.category === 'highest_debt') return 'row-highest-debt';
      return '';
    }

    // Normal view: priority-based coloring
    if (highlightIds.no_recent_payment?.has(id)) return 'row-no-payment';
    if (highlightIds.highest_debt?.has(id)) return 'row-highest-debt';
    return '';
  }, [highlightIds, insightFilter]);

  // User-specific storage keys for state persistence (prevents data leakage between users)
  // Include user ID in all keys to ensure settings are user-specific
  const userId = userProfile?.username || 'unknown';

  // localStorage = User preferences (persists across sessions)
  const GRID_STATE_KEY = `clients-datagrid-state-${userId}`;  // localStorage
  const PAGE_SIZE_KEY = `clients-page-size-${userId}`;  // localStorage

  // sessionStorage = Work context (clears when tab closes)
  const PAGE_NUMBER_KEY = `clients-page-number-${userId}`;  // sessionStorage
  const GROUP_FILTER_KEY = `clients-selected-group-${userId}`;  // sessionStorage
  const DEBT_FILTER_KEY = `clients-debt-filter-${userId}`;  // sessionStorage
  const SORT_OVERRIDE_KEY = `clients-sort-override-${userId}`;  // sessionStorage + 24h TTL

  // ==================== State for group filtering ====================
  // Persisted state for group filter (sessionStorage - clears on tab close)
  const [selectedGroup, setSelectedGroup] = useState(() => {
    const saved = sessionStorage.getItem(GROUP_FILTER_KEY);
    return saved || null;  // Returns null if empty, not string "null"
  });

  const [availableGroups, setAvailableGroups] = useState([]);

  // ==================== State for debt filtering ====================
  // Persisted state for debt filters (sessionStorage - clears on tab close)
  // Now supports multiple filters as an array
  const [debtFilters, setDebtFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem(DEBT_FILTER_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Handle migration from old single-filter format
        if (parsed && !Array.isArray(parsed) && parsed.field) {
          return [parsed];
        }
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      console.error('Failed to load debt filters from sessionStorage', e);
    }
    return [];
  });

  // ====== DIALOG STATE ======
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [chargeDialogOpen, setChargeDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [selectedActionClient, setSelectedActionClient] = useState(null);

  // Quick actions menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuClient, setActionMenuClient] = useState(null);

  const handleActionMenuOpen = useCallback((event, client) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuClient(client);
  }, []);

  const handleActionMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setActionMenuClient(null);
  }, []);

  const handleQuickAction = useCallback((type) => {
    setSelectedActionClient(actionMenuClient);
    handleActionMenuClose();
    if (type === 'payment') setPaymentDialogOpen(true);
    else if (type === 'charge') setChargeDialogOpen(true);
    else if (type === 'credit') setCreditDialogOpen(true);
  }, [actionMenuClient, handleActionMenuClose]);

  const invalidateClientListCache = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['clients'], refetchType: 'active' });
  }, [queryClient]);

  const fetchTransactionsNoop = useCallback(() => {}, []);

  // ====== AUTOCOMPLETE SEARCH STATE ======
  const [debouncedAutocompleteSearch, setDebouncedAutocompleteSearch] = useState('');
  const [selectedValue, setSelectedValue] = useState(null); // Track selected client object


  
  // API ref for DataGridPro
  const apiRef = useGridApiRef();

  // Sort lives in sessionStorage with a 24h TTL: it survives in-app navigation
  // within the tab but reverts to the recency default when the tab closes or
  // a day passes. Default = most-recently-interacted clients first.
  // Declared BEFORE `initialGridState` because the memo reads it.
  const [ordering, setOrdering] = useState(
    () => readSessionSort(SORT_OVERRIDE_KEY) || DEFAULT_CLIENTS_ORDERING
  );

  // Load initial grid state from localStorage. Sorting is NOT read from
  // localStorage anymore — it lives in sessionStorage (per SORT_OVERRIDE_KEY)
  // Density AND column state (show/hide + ordering + widths) are loaded from
  // localStorage; sorting/pagination are intentionally NOT in initialState.
  // They're driven by the controlled `sortModel` and `paginationModel` props —
  // mixing initialState with controlled props for the same feature is a known
  // MUI footgun (the uncontrolled snapshot can freeze the column-header arrow
  // on the first sort it saw). Column visibility has no controlled prop here,
  // so restoring it via initialState is the recommended MUI pattern and is
  // what makes the user's show/hide column selection persist across reloads.
  const initialGridState = React.useMemo(() => {
    let density = 'comfortable';
    let columns;
    try {
      const stateJSON = localStorage.getItem(GRID_STATE_KEY);
      if (stateJSON) {
        const parsed = JSON.parse(stateJSON);
        density = parsed.density || 'comfortable';
        // Restore saved column state (columnVisibilityModel / orderedFields /
        // dimensions). Unknown fields — e.g. a config-driven column that no
        // longer exists — are ignored by the grid, so this is safe even when
        // the group's ui_config changes.
        if (parsed.columns) {
          columns = parsed.columns;
          // One-time layout migration: "Last Active" moved from after the
          // ID column to the end. Saved orderedFields would pin it in the old
          // spot, so move it to the end once; later user reorders are kept.
          if ((parsed.layoutVersion || 1) < GRID_LAYOUT_VERSION
              && Array.isArray(columns.orderedFields)) {
            const rest = columns.orderedFields.filter((f) => f !== 'last_interaction_at');
            columns = { ...columns, orderedFields: [...rest, 'last_interaction_at'] };
          }
        }
      }
    } catch (e) {
      console.error("Failed to load grid state from localStorage", e);
    }
    return columns ? { density, columns } : { density };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination state (server-side) - split between localStorage and sessionStorage
  // pageSize = localStorage (user preference, persists forever)
  // page = sessionStorage (work context, resets on tab close)
  const [paginationModel, setPaginationModel] = useState(() => {
    let pageSize = 25;  // default
    let page = 0;  // default

    try {
      // Load pageSize from localStorage (user preference)
      const savedPageSize = localStorage.getItem(PAGE_SIZE_KEY);
      if (savedPageSize) {
        const parsedPageSize = parseInt(savedPageSize, 10);
        if ([25, 50, 100].includes(parsedPageSize)) {
          pageSize = parsedPageSize;
        }
      }

      // Load page from sessionStorage (work context)
      const savedPage = sessionStorage.getItem(PAGE_NUMBER_KEY);
      if (savedPage) {
        const parsedPage = parseInt(savedPage, 10);
        if (parsedPage >= 0) {
          page = parsedPage;
        }
      }
    } catch (e) {
      console.error('Failed to load pagination from storage', e);
    }

    return { page, pageSize };
  });

  const handleShowAll = useCallback((category) => {
    const insightsCache = queryClient.getQueryData(['clients', 'insights']);
    if (!insightsCache) return;

    let ids = [];
    let label = '';
    if (category === 'no_recent_payment') {
      ids = insightsCache.no_recent_payment_ids || [];
      label = 'לא שילמו לאחרונה';
    } else if (category === 'highest_debt') {
      ids = insightsCache.highest_debt_ids || (insightsCache.highest_debt || []).map(c => c.ClientBusinessID);
      label = 'חוב גבוה';
    } else if (category === 'most_active') {
      ids = insightsCache.most_active_ids || (insightsCache.most_active || []).map(c => c.ClientBusinessID);
      label = 'הכי פעילים';
    } else if (category === 'least_active') {
      ids = insightsCache.least_active_ids || (insightsCache.least_active || []).map(c => c.ClientBusinessID);
      label = 'הכי פחות פעילים';
    }

    if (ids.length > 0) {
      setInsightFilter({ category, ids, label });
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }

    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [queryClient]);

  const clearInsightFilter = useCallback(() => {
    setInsightFilter(null);
  }, []);

  const handleGroupChange = (newGroup) => {
    const groupValue = newGroup || null;
    setSelectedGroup(groupValue);

    // Save to sessionStorage (clears on tab close)
    if (groupValue) {
      sessionStorage.setItem(GROUP_FILTER_KEY, groupValue);
    } else {
      sessionStorage.removeItem(GROUP_FILTER_KEY);
    }
  };

  // Handle debt filter changes (now accepts array of filters)
  const handleDebtFilterApply = (newFilters) => {
    setDebtFilters(newFilters);
    // Reset to page 0 when filter changes
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };
  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };
  const handleClientCreated = () => {
    // Reset to page 0 after creating a client so user sees the new client
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Fetch available groups from backend
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await API.get('/groups/');
        setAvailableGroups(response.data);
      } catch (error) {
        console.error('Error fetching groups:', error);
      }
    };
    fetchGroups();
  }, []);

  // Debounce search term for DataGrid
  React.useEffect(() => {
    // Skip reset on initial mount to preserve cached pagination
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      // Reset pagination immediately when search changes (not debounced)
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }

    // Debounce the actual search term update
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounced callback for autocomplete search (avoids lag while typing)
  const debouncedSetAutocompleteSearch = React.useCallback((value) => {
    const timer = setTimeout(() => {
      setDebouncedAutocompleteSearch(value);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Ref to track the cleanup function
  const autocompleteTimerCleanup = React.useRef(null);

  // Reset to page 0 when group filter changes (but not on initial mount)
  const isInitialGroupMount = React.useRef(true);
  React.useEffect(() => {
    if (isInitialGroupMount.current) {
      isInitialGroupMount.current = false;
    } else {
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  }, [selectedGroup]);

  // Save pagination to appropriate storage
  // pageSize → localStorage (user preference, persists forever)
  // page → sessionStorage (work context, resets on tab close)
  React.useEffect(() => {
    try {
      localStorage.setItem(PAGE_SIZE_KEY, paginationModel.pageSize.toString());
      sessionStorage.setItem(PAGE_NUMBER_KEY, paginationModel.page.toString());
    } catch (e) {
      console.error('Failed to save pagination to storage', e);
    }
  }, [paginationModel]);

  const handleStateChange = React.useCallback(() => {
    if (apiRef.current) {
      try {
        const state = apiRef.current.exportState();

        // Sorting is intentionally NOT persisted here — it lives in
        // sessionStorage so it resets per browser tab / per day. We only
        // save density and column state to localStorage.
        const stateToSave = {
          density: state.density,
          columns: state.columns,
          layoutVersion: GRID_LAYOUT_VERSION,
        };

        localStorage.setItem(GRID_STATE_KEY, JSON.stringify(stateToSave));
      } catch (error) {
        console.error('Failed to save grid state:', error);
      }
    }
  }, [apiRef, GRID_STATE_KEY]);

  // Persist `ordering` to sessionStorage whenever the user changes it.
  // When sort is reset to default, clear the key entirely so a fresh tab
  // starts clean (no zombie entry).
  useEffect(() => {
    if (ordering === DEFAULT_CLIENTS_ORDERING) {
      clearSessionSort(SORT_OVERRIDE_KEY);
    } else {
      writeSessionSort(SORT_OVERRIDE_KEY, ordering);
    }
  }, [ordering, SORT_OVERRIDE_KEY]);

  // Handle sort model changes (skip reset on initial grid load)
  const isInitialSortMount = React.useRef(true);
  const handleSortModelChange = React.useCallback((newSortModel) => {
    const newOrdering = getOrderingFromSortModel(newSortModel);
    setOrdering(newOrdering);

    // Skip reset on initial mount to preserve cached pagination
    if (isInitialSortMount.current) {
      isInitialSortMount.current = false;
    } else {
      setPaginationModel(prev => ({ ...prev, page: 0 }));  // Reset to page 0 on sort change
    }
  }, []);

  // Memoized so we don't pass a fresh array reference into DataGridPro
  // on every render — keeps internal grid memoizations stable.
  const sortModel = useMemo(() => getSortModelFromOrdering(ordering), [ordering]);

  // ==================== Mobile Sort ====================
  // Desktop uses DataGridPro column-header sort; mobile has no headers, so we
  // expose a dedicated "Sort by" dropdown in the toolbar that drives the same
  // `ordering` state and persists via the same `GRID_STATE_KEY` localStorage entry.
  const mobileSortOptions = useMemo(() => {
    // Read directly from userProfile (stable ref) to avoid re-deriving deps on every render
    const cfs = userProfile?.ui_config?.client_fields_schema || {};
    const dfs = userProfile?.ui_config?.debt_fields_schema || {};
    const df = userProfile?.ui_config?.debt_fields || [];
    const nameLabel = cfs?.ClientName?.label || 'שם הלקוח';
    const opts = [
      { key: 'last_interaction_at_desc', label: 'פעילות אחרונה (חדש→ישן)', field: 'last_interaction_at', direction: 'desc' },
      { key: 'ClientName_asc',  label: `${nameLabel} (א-ת)`, field: 'ClientName', direction: 'asc'  },
      { key: 'ClientName_desc', label: `${nameLabel} (ת-א)`, field: 'ClientName', direction: 'desc' },
    ];
    if (df.length > 0) {
      const debtLabel = dfs?.TotalDebt?.label || 'חוב כולל';
      opts.push(
        { key: 'TotalDebt_desc', label: `${debtLabel} - מהגבוה לנמוך`, field: 'TotalDebt', direction: 'desc' },
        { key: 'TotalDebt_asc',  label: `${debtLabel} - מהנמוך לגבוה`, field: 'TotalDebt', direction: 'asc'  },
      );
    }
    return opts;
  }, [userProfile]);

  // Derive the currently selected sort key from the shared `ordering` state
  // so the dropdown reflects whatever was last picked (on desktop OR mobile).
  const selectedSortKey = useMemo(() => {
    if (!ordering) return 'last_interaction_at_desc';
    const isDesc = ordering.startsWith('-');
    const field = isDesc ? ordering.slice(1) : ordering;
    const key = `${field}_${isDesc ? 'desc' : 'asc'}`;
    return mobileSortOptions.some((o) => o.key === key) ? key : 'last_interaction_at_desc';
  }, [ordering, mobileSortOptions]);

  const handleMobileSortChange = useCallback((newKey) => {
    const opt = mobileSortOptions.find((o) => o.key === newKey);
    if (!opt) return;

    const newOrdering = opt.direction === 'desc' ? `-${opt.field}` : opt.field;
    setOrdering(newOrdering);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    // sessionStorage write is handled by the useEffect on `ordering`
  }, [mobileSortOptions]);

  const groupName = useMemo(() => {
    if (
      !selectedGroup ||
      ['NO_FLOW', 'NO_YEAR', 'NO_GENERAL', 'NO_GROUP'].includes(selectedGroup)
    ) {
      return '';
    }
    const group = availableGroups.find((g) => String(g.GroupID) === selectedGroup);
    return group ? group.GroupName : '';
  }, [selectedGroup, availableGroups]);

  // ==================== REACT QUERY SETUP ====================
  // Autocomplete query - server-side search with debounce
  const {
    data: autocompleteClients = [],
    isLoading: isLoadingAutocomplete,
  } = useQuery({
    queryKey: ['clients', 'autocomplete', debouncedAutocompleteSearch],
    queryFn: () => fetchClientsAutocomplete(debouncedAutocompleteSearch),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Create options with dummy "no results" record when empty (and user is searching)
  const autocompleteOptions = useMemo(() => {
    if (autocompleteClients.length === 0 && debouncedAutocompleteSearch) {
      return [{ ClientBusinessID: -1, ClientName: 'אין תוצאות', isNoResults: true }];
    }
    return autocompleteClients;
  }, [autocompleteClients, debouncedAutocompleteSearch]);

  // Paginated query - fetches current page with full data
  const {
    data: paginatedData,
    isLoading: loading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: [
      'clients',
      'paginated',
      paginationModel.page + 1,
      paginationModel.pageSize,
      debouncedSearchTerm,
      selectedGroup,
      ordering,
      JSON.stringify(debtFilters),
      insightFilter ? insightFilter.category : null,
    ],
    queryFn: () => fetchPaginatedClients({
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      search: debouncedSearchTerm,
      groupFilter: selectedGroup || '',
      ordering: ordering,
      debtFilters: debtFilters,
      clientIds: insightFilter?.ids || null,
    }),
    placeholderData: keepPreviousData,  // Smooth transitions between pages
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Extract data from paginated response
  const clientsData = paginatedData?.results || [];
  const totalRows = paginatedData?.count || 0;

  // ==================== Colors for chips ====================
  const getChipColors = (groupType) => {
    switch (groupType) {
      case 'Flow':
        return { backgroundColor: '#e3f2fd', color: '#1976d2' };
      case 'Year':
        return { backgroundColor: '#fff3e0', color: '#fb8c00' };
      case 'General':
        return { backgroundColor: '#e8f5e9', color: '#43a047' };
      default:
        return { backgroundColor: '#f8f9fa', color: '#1976d2' };
    }
  };

  const dynamicClientColumns = useMemo(() => {
    return clientExtraColumns.map((clientFieldKey) => {
      const schema = clientFieldsSchema[clientFieldKey] || {};
      const label = schema.label || clientFieldKey;
      const type = schema.type || 'string';

      // Create base column definition
      const column = {
        field: clientFieldKey,
        headerName: label,
        flex: 1,
        minWidth: 120,
        align: 'center',
        headerAlign: 'center',
        mobile: false,
      };

      // Add special formatting based on field type
      if (type === 'number') {
        column.valueGetter = (value, row) => parseFloat(row[clientFieldKey] || '0');
        column.renderCell = (params) => {
          const raw = parseFloat(params.row[clientFieldKey] || '0');
          return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(raw);
        };
      } else if (type === 'date') {
        column.renderCell = (params) => {
          const dateValue = params.row[clientFieldKey];
          if (!dateValue) return '';
          return new Date(dateValue).toLocaleDateString('he-IL');
        };
      } else if (type === 'boolean') {
        column.renderCell = (params) => {
          return params.row[clientFieldKey] ? 'כן' : 'לא';
        };
      }
      // For string and text types, default rendering is fine

      return column;
    });
  }, [clientExtraColumns, clientFieldsSchema]);

  // ==================== Dynamic Debt Columns ====================
  const dynamicDebtColumns = useMemo(() => {
    return debtFields.map((debtFieldKey) => {
      const schema = debtFieldsSchema[debtFieldKey] || {};
      const label = schema.label || debtFieldKey;

      if (debtFieldKey === 'TotalDebt') {
        return {
          field: 'TotalDebt',
          headerName: label,
          flex: 1,
          minWidth: 120,
          align: 'center',
          headerAlign: 'center',
          valueGetter: function (value, row) {
            if (!row || !row.debt || !row.debt.TotalDebt) {
              return 0;
            }
            return parseFloat(row.debt.TotalDebt) || 0;
          },
          renderCell: (params) => {
            const raw = parseFloat(params.row?.debt?.TotalDebt || '0');
            return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(raw);
          },
        };
      } else {
        return {
          field: debtFieldKey,
          headerName: label,
          flex: 1,
          minWidth: 120,
          align: 'center',
          headerAlign: 'center',
          mobile: false,
          valueGetter: (value, row) =>
            parseFloat(row?.debt?.DebtFields?.[debtFieldKey] || '0'),
          renderCell: (params) => {
            const raw = parseFloat(params.row?.debt?.DebtFields?.[debtFieldKey] || '0');
            return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(raw);
          },
        };
      }
    });
  }, [debtFields, debtFieldsSchema]);

  // ==================== Base Columns ====================
  // We'll push 'groups' column conditionally
  const baseColumns = [
    {
      field: 'ClientName',
      headerName: clientFieldsSchema?.ClientName?.label || 'שם הלקוח',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params?.row || {};
        const canNavigateToClient = ClientAllowedRoles.includes(role);
        const fullName = row.ClientName || '';

        const tooltipTitle = (
          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 'bold',
              lineHeight: 1.4,
              px: 1,
              py: 0.5,
            }}
          >
            {fullName}
          </Typography>
        );

        const inner = canNavigateToClient ? (
          <Button
            variant="text"
            color="primary"
            onClick={() => navigate(`/client/${row.ClientBusinessID}`)}
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block',
            }}
          >
            {fullName}
          </Button>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              textAlign: 'center',
              fontSize: '1.1rem',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fullName}
          </Typography>
        );

        return (
          <Tooltip
            title={tooltipTitle}
            arrow
            placement="top"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 3,
                  maxWidth: 'none',
                },
              },
              arrow: {
                sx: { color: 'background.paper' },
              },
            }}
          >
            {inner}
          </Tooltip>
        );
      },
    },
    {
      field: 'ClientBusinessID',
      headerName: clientFieldsSchema?.ClientBusinessID?.label || 'תז/חפ',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  // "Last Active" column — sortable, hideable, low-profile metadata.
  // Rendered LAST (after the debt columns, see `columns` below): it explains
  // the default sort rather than identifying the client, so it stays out of
  // the name → ID → debt scan path and uses muted, smaller text.
  const lastActiveColumn = {
    field: 'last_interaction_at',
    headerName: 'פעילות אחרונה',
    width: 115,
    align: 'center',
    headerAlign: 'center',
    mobile: false,
    sortable: true,
    type: 'dateTime',
    valueGetter: (value, row) => {
      const raw = row?.last_interaction_at;
      return raw ? new Date(raw) : null;
    },
    renderCell: (params) => {
      const d = params.value;
      if (!d) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.9rem' }}>
              —
            </Typography>
          </Box>
        );
      }
      const relative = formatDistanceToNow(d, { addSuffix: true, locale: he });
      const absolute = formatDate(d, 'dd/MM/yyyy HH:mm');
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <Tooltip title={absolute} arrow placement="top" enterDelay={300}>
            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary', textAlign: 'center' }}>
              {relative}
            </Typography>
          </Tooltip>
        </Box>
      );
    },
  };

  if (uiConfig.include_groups) {
    baseColumns.push({
      field: 'groups',
      headerName: 'קבוצות',
      flex: 1,
      minWidth: 230,
      mobile: false,
      renderCell: (params) => {
        const row = params.row;
        const groups = [];
        if (row.FlowGroup) groups.push(row.FlowGroup);
        if (row.YearGroup) groups.push(row.YearGroup);
        if (row.GeneralGroups && row.GeneralGroups.length > 0) {
          groups.push(...row.GeneralGroups);
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {groups.length > 0
              ? groups.map((group) => {
                  const colors = getChipColors(group.GroupType);
                  return groupAccessRoles.includes(role) ? (
                    <Chip
                      key={group.GroupID}
                      component={Link}
                      to={`/group/${group.GroupID}`}
                      clickable
                      label={group.GroupName}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        padding: '2px 4px',
                        backgroundColor: colors.backgroundColor,
                        color: colors.color,
                        borderColor: colors.color,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#e9ecef',
                          color: colors.color,
                          '& .MuiChip-label': {
                            textDecoration: 'underline',
                          },
                        },
                      }}
                    />
                  ) : (
                    <Chip
                      key={group.GroupID}
                      label={group.GroupName}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        padding: '2px 4px',
                        backgroundColor: colors.backgroundColor,
                        color: colors.color,
                        borderColor: colors.color,
                      }}
                    />
                  );
                })
              : 'לא הוגדר'}
          </Box>
        );
      },
    });
  }

  // ==================== Quick Actions Column ====================
  if (showActions) {
    baseColumns.unshift({
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleActionMenuOpen(e, params.row)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    });
  }

  // ==================== Final Columns ====================
  const columns = useMemo(() => {
    return [...baseColumns, ...dynamicClientColumns, ...dynamicDebtColumns, lastActiveColumn];
  }, [baseColumns, dynamicClientColumns, dynamicDebtColumns, lastActiveColumn]);

  // ==================== Mobile Card Renderer ====================
  const clientCardRenderer = useCallback((row) => {
    const totalDebt = parseFloat(row?.debt?.TotalDebt || '0');
    const formattedDebt = new Intl.NumberFormat('he-IL', {
      style: 'currency', currency: 'ILS', minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(totalDebt);

    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.ClientName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {clientFieldsSchema?.ClientBusinessID?.label || 'תז/חפ'}: {row.ClientBusinessID}
          </Typography>
        </Box>
        {debtFields.length > 0 && (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bold',
              color: totalDebt > 0 ? 'error.main' : 'success.main',
              flexShrink: 0,
            }}
          >
            {formattedDebt}
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); handleActionMenuOpen(e, row); }}
          aria-label="פעולות מהירות"
          sx={{ flexShrink: 0 }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
    );
  }, [clientFieldsSchema, debtFields, handleActionMenuOpen]);

  return (
    <ThemeProvider theme={theme}>
      <div dir="rtl">
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, sm: 2 } }}>
          <Typography variant="h3" sx={{ mb: { xs: 1.5, md: 3 }, fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}>
            לקוחות
          </Typography>

          {showSummary && <ClientsSummaryBar />}
          {showSummary && (
            <ClientInsightsPanel
              userId={userId}
              noPaymentMonthsThreshold={noPaymentMonthsThreshold}
              highDebtThreshold={highDebtThreshold}
              rowHighlightCategories={rowHighlightCategories}
              clientDetailAllowed={ClientAllowedRoles.includes(role)}
              onHighlightIdsChange={handleHighlightIdsChange}
              onShowAll={handleShowAll}
              isManager={role === 'manager'}
            />
          )}

          <Card ref={gridRef} sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
              title={
                <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.3rem' } }}>
                  רשימת לקוחות
                </Typography>
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: { xs: 1, sm: 1.5 },
                px: { xs: 1.5, sm: 2 },
              }}
            />
            <CardContent>
              {/* Search, Group Filter & Create-Button row */}
              <ResponsiveToolbar>
                <Autocomplete
                  freeSolo
                  options={autocompleteOptions}
                  loading={isLoadingAutocomplete}
                  loadingText="טוען..."
                  openOnFocus
                  filterOptions={(x) => x}  // Disable client-side filtering (we do server-side)
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.isNoResults) return option.ClientName; // Show "אין תוצאות"
                    return `${option.ClientName} (${option.ClientBusinessID})`;
                  }}
                  getOptionDisabled={(option) => option.isNoResults === true}  // Make "no results" unselectable
                  value={selectedValue}  // Only control selected value, not input text
                  onInputChange={(event, newInputValue, reason) => {
                    // Clear previous timer
                    if (autocompleteTimerCleanup.current) {
                      autocompleteTimerCleanup.current();
                    }

                    // Debounce the API call - but DON'T update React state on every keystroke
                    autocompleteTimerCleanup.current = debouncedSetAutocompleteSearch(newInputValue);

                    // Clear searchTerm if input is emptied (manually deleted all text)
                    if (newInputValue.trim() === '' && searchTerm !== '') {
                      setSearchTerm('');
                      setSelectedValue(null);
                    }
                  }}
                  onChange={(event, newValue) => {
                    // Ignore dummy "no results" record
                    if (newValue && typeof newValue === 'object' && newValue.isNoResults) {
                      return;
                    }

                    if (typeof newValue === 'string' && newValue.trim() !== '') {
                      // Typed and pressed enter - NOW update DataGrid search
                      setSearchTerm(newValue);
                      setSelectedValue(null); // Clear selected object
                    } else if (newValue && typeof newValue === 'object') {
                      // Selected an option - NOW update DataGrid search by business ID
                      setSearchTerm(newValue.ClientBusinessID);
                      setSelectedValue(newValue); // Track selected object
                      // Reset ordering to default when selecting from autocomplete
                      setOrdering(DEFAULT_CLIENTS_ORDERING);
                    } else {
                      // Cleared or empty string - clear DataGrid search and autocomplete search
                      setSearchTerm('');
                      setSelectedValue(null);
                      setDebouncedAutocompleteSearch('');
                      // Clear any pending timer
                      if (autocompleteTimerCleanup.current) {
                        autocompleteTimerCleanup.current();
                      }
                    }
                  }}
                  forcePopupIcon={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`חיפוש לפי שם או ${clientFieldsSchema?.ClientBusinessID?.label || 'מזהה'}`}
                      placeholder={`הקלד שם או ${clientFieldsSchema?.ClientBusinessID?.label || 'מזהה'}`}
                      size="small"
                      sx={{ width: { xs: '100%', md: 250 } }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingAutocomplete ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* SHOW GROUP FILTER ONLY IF include_groups === true */}
                {uiConfig.include_groups && (
                  <TextField
                    select
                    label="סינון לפי קבוצה"
                    size="small"
                    value={selectedGroup || ''}
                    onChange={(e) => handleGroupChange(e.target.value || null)}
                    sx={{ width: { xs: '100%', md: 250 } }}
                  >
                    <MenuItem value="">כל הקבוצות</MenuItem>
                    <MenuItem value="NO_GROUP">ללא כל קבוצה</MenuItem>
                    {/* For each group type, add a "NO_TYPE" entry using its label */}
                    {Array.isArray(uiConfig.group_types) &&
                      uiConfig.group_types.map((type) => {
                        const label = uiConfig.groups_schemas?.[type]?.label || type;
                        return (
                          <MenuItem
                            key={`no_${type}`}
                            value={`NO_${type.toUpperCase()}`}
                          >
                            {`ללא קבוצה ${label}`}
                          </MenuItem>
                        );
                      })}
                    {/* Then list actual groups from the server */}
                    {availableGroups.map((group) => (
                      <MenuItem key={group.GroupID} value={String(group.GroupID)}>
                        {group.GroupName}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {/* Mobile-only sort dropdown — desktop uses DataGridPro column-header sort */}
                <TextField
                  select
                  label="מיון"
                  size="small"
                  value={selectedSortKey}
                  onChange={(e) => handleMobileSortChange(e.target.value)}
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    gridColumn: { xs: '1 / -1', md: 'auto' },
                    width: { xs: '100%', md: 250 },
                    minWidth: { xs: 0, md: 250 },
                  }}
                >
                  {mobileSortOptions.map((opt) => (
                    <MenuItem key={opt.key} value={opt.key}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                <ToolbarActions>
                  <Button variant="outlined" onClick={() => {
                    const params = new URLSearchParams();
                    if (selectedGroup) params.append('group', selectedGroup);
                    if (groupName) params.append('groupName', groupName);
                    if (ordering) params.append('ordering', ordering);
                    const validFilters = debtFilters.filter(f => f.field && f.threshold);
                    if (validFilters.length > 0) params.append('debtFilters', JSON.stringify(validFilters));
                    navigate('/clients/minimal-print?' + params.toString());
                  }}>
                    הדפסה
                  </Button>
                  { createClientRoles.includes(role) && (
                    <Button variant="contained" color="primary" onClick={handleOpenCreateDialog}>
                      הוסף לקוח חדש
                    </Button>
                  )}
                  {debtFields.length > 0 && (
                    <DebtFilterPanel
                      onApply={handleDebtFilterApply}
                      appliedFilters={debtFilters}
                      debtFields={debtFields}
                      debtFieldsSchema={debtFieldsSchema}
                      storageKey={DEBT_FILTER_KEY}
                    />
                  )}
                </ToolbarActions>
              </ResponsiveToolbar>

              {/* Active insight filter indicator */}
              {insightFilter && (
                <Box dir="rtl" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
                  <Chip
                    label={`מסנן: ${insightFilter.label} (${insightFilter.ids.length})`}
                    onDelete={clearInsightFilter}
                    color="warning"
                    variant="outlined"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              )}

              {/* DataGrid */}
              <Box
                sx={{
                  width: '100%',
                  '.row-no-payment': {
                    backgroundColor: '#fff3e0 !important',
                    '&:hover': { backgroundColor: '#ffe0b2 !important' },
                  },
                  '.row-highest-debt': {
                    backgroundColor: '#ffebee !important',
                    '&:hover': { backgroundColor: '#ffcdd2 !important' },
                  },
                  '.MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                    fontSize: '1.15rem',
                    whiteSpace: 'normal !important',
                    lineHeight: '1.2',
                    overflow: 'visible',
                  },
                  '.MuiDataGrid-cell': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    fontSize: '1rem',
                  },
                }}
              >
                <ResponsiveDataView
                    rows={clientsData}
                    columns={columns}
                    cardRenderer={clientCardRenderer}
                    onRowClick={(row) => navigate(`/client/${row.ClientBusinessID}`)}
                    getRowId={(row) => row.ClientBusinessID}
                    loading={loading || isFetching}
                    emptyMessage="אין תוצאות"
                    mobilePage={paginationModel.page + 1}
                    mobilePageSize={paginationModel.pageSize}
                    mobileTotalRows={totalRows}
                    onMobilePageChange={(page) => setPaginationModel((prev) => ({ ...prev, page: page - 1 }))}

                    // DataGridPro props (desktop only)
                    apiRef={apiRef}
                    onStateChange={handleStateChange}
                    density="comfortable"
                    getRowClassName={showSummary ? getRowClassName : undefined}
                    autoHeight
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    rowCount={totalRows}
                    pageSizeOptions={[25, 50, 100]}
                    pagination
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    sortingOrder={['asc', 'desc']}
                    initialState={initialGridState}
                    disableSelectionOnClick
                    localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                    slots={{
                      toolbar: () => (
                        <CustomToolbar
                          rows={clientsData}
                          columns={columns}
                          isExporting={isExporting}
                          setIsExporting={setIsExporting}
                          selectedGroup={selectedGroup}
                          groupName={groupName}
                          debtFilters={debtFilters}
                          ordering={ordering}
                        />
                      ),
                    }}
                    components={{
                      NoRowsOverlay: () => (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          אין תוצאות
                        </Box>
                      ),
                    }}
                  />
              </Box>
            </CardContent>
          </Card>
        </Container>

        {/* SEPARATE DIALOG COMPONENT */}
        {openCreateDialog && (
          <ClientCreateDialog
            open={openCreateDialog}
            onClose={handleCloseCreateDialog}
            onClientCreated={handleClientCreated}
            userProfile={userProfile}
          />
        )}

        {/* QUICK ACTIONS MENU */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => handleQuickAction('payment')}>תשלום</MenuItem>
          <MenuItem onClick={() => handleQuickAction('charge')}>חיוב</MenuItem>
          <MenuItem onClick={() => handleQuickAction('credit')}>זיכוי</MenuItem>
        </Menu>

        {/* QUICK ACTION DIALOGS */}
        {paymentDialogOpen && (
          <PaymentDialog
            open={paymentDialogOpen}
            onClose={() => { setPaymentDialogOpen(false); setSelectedActionClient(null); }}
            debtFields={debtFields}
            debtFieldsSchema={debtFieldsSchema}
            fetchTransactions={fetchTransactionsNoop}
            invalidateClientListCache={invalidateClientListCache}
            userProfile={userProfile}
            initialClient={selectedActionClient}
          />
        )}
        {chargeDialogOpen && (
          <ChargeDialog
            open={chargeDialogOpen}
            onClose={() => { setChargeDialogOpen(false); setSelectedActionClient(null); }}
            debtFields={debtFields}
            debtFieldsSchema={debtFieldsSchema}
            fetchTransactions={fetchTransactionsNoop}
            invalidateClientListCache={invalidateClientListCache}
            userProfile={userProfile}
            initialClient={selectedActionClient}
          />
        )}
        {creditDialogOpen && (
          <CreditDialog
            open={creditDialogOpen}
            onClose={() => { setCreditDialogOpen(false); setSelectedActionClient(null); }}
            debtFields={debtFields}
            debtFieldsSchema={debtFieldsSchema}
            fetchTransactions={fetchTransactionsNoop}
            invalidateClientListCache={invalidateClientListCache}
            userProfile={userProfile}
            initialClient={selectedActionClient}
          />
        )}
      </div>
    </ThemeProvider>
  );
};

export default ClientsListPageCached;
